/**
 * Dazm: "choose your options first" product pages (template product.variant-gallery).
 *
 * The page opens with no option selected: the server renders the variant picker without
 * checked buttons and marks it with data-dazm-require-selection (see dazm-variant-main-picker.liquid).
 * While some options are still missing:
 *   - the theme's variant request is held back (no variant is chosen for the shopper),
 *   - the add to cart button stays disabled and says which options are missing,
 *   - when the image option (Color) is chosen, the media gallery switches to that color.
 * When the last missing option is chosen, the normal theme flow takes over and renders the
 * real variant (price, button, gallery, URL ?variant=).
 *
 * The color gallery (dazm-media-gallery-content.liquid) keeps the main image first, so the
 * selected color's front image is not slide 0. The theme's slideshow does not scroll to a
 * non-zero initial slide by itself, so alignGallery() does it.
 */

const PICKER_SELECTOR = 'variant-picker[data-dazm-require-selection]';

/** @type {WeakMap<HTMLElement, AbortController>} */
const galleryRequests = new WeakMap();

/** @param {HTMLElement} picker */
function radioFieldsets(picker) {
  return Array.from(picker.querySelectorAll('fieldset')).filter((fieldset) =>
    fieldset.querySelector('input[type="radio"]')
  );
}

/** @param {Element} fieldset */
function optionNameOf(fieldset) {
  const input = fieldset.querySelector('input[type="radio"]');
  return input instanceof HTMLInputElement ? input.dataset.optionName || '' : '';
}

/** @param {HTMLElement} picker */
function missingOptions(picker) {
  return radioFieldsets(picker)
    .filter((fieldset) => !fieldset.querySelector('input[type="radio"]:checked'))
    .map(optionNameOf);
}

/** @param {HTMLElement} picker */
function chosenOptions(picker) {
  /** @type {Record<string, string>} */
  const chosen = {};
  picker.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
    if (input instanceof HTMLInputElement && input.dataset.optionName) chosen[input.dataset.optionName] = input.value;
  });
  return chosen;
}

/** @param {HTMLElement} picker */
function scopeOf(picker) {
  return picker.closest('.shopify-section') || document;
}

/** @param {HTMLElement} picker */
function variantsOf(picker) {
  try {
    return JSON.parse(picker.dataset.dazmVariants || '[]');
  } catch (error) {
    return [];
  }
}

/**
 * Finds the variant matching every chosen option.
 * @param {HTMLElement} picker
 * @param {Record<string, string>} chosen
 */
function findVariant(picker, chosen) {
  const names = Object.keys(chosen);
  return variantsOf(picker).find((/** @type {any} */ variant) =>
    names.every((name) => variant.options[name] === undefined || variant.options[name] === chosen[name])
  );
}

/**
 * Disables the buy buttons and shows which options are missing.
 * @param {HTMLElement} picker
 */
function lockBuyButtons(picker) {
  const missing = missingOptions(picker);
  const label = missing.length ? 'Select ' + missing.join(' & ') : '';

  scopeOf(picker)
    .querySelectorAll('product-form-component [ref="addToCartButton"]')
    .forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = true;
      button.classList.add('dazm-needs-selection');

      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim()
      );
      if (textNode && label) textNode.textContent = label;
    });

  scopeOf(picker)
    .querySelectorAll('product-form-component [ref="acceleratedCheckoutButtonContainer"]')
    .forEach((container) => container.setAttribute('hidden', 'true'));
}

/**
 * Scrolls a color gallery to its initial slide (the selected color's front image).
 * @param {Element} gallery
 */
function alignGallery(gallery) {
  if (!(gallery instanceof HTMLElement) || gallery.dataset.dazmAligned) return;
  const index = parseInt(gallery.dataset.dazmInitialSlide || '0', 10);
  if (!index) return;
  gallery.dataset.dazmAligned = 'true';

  let tries = 0;
  const attempt = () => {
    if (!gallery.isConnected) return;
    const slideshow = /** @type {any} */ (gallery.querySelector('slideshow-component'));
    const scroller = slideshow ? slideshow.querySelector('slideshow-slides') : null;
    const ready = slideshow && scroller && typeof slideshow.select === 'function' && scroller.clientWidth > 0;

    if (!ready) {
      if (tries++ < 60) setTimeout(attempt, 50);
      return;
    }
    if (Math.abs(scroller.scrollLeft) > 1) return;

    slideshow.current = 0;
    slideshow.select(index, undefined, { animate: false });
  };
  setTimeout(attempt, 60);
}

/** @param {ParentNode} root */
function alignGalleriesIn(root) {
  if (root instanceof Element && root.matches('media-gallery[data-dazm-initial-slide]')) alignGallery(root);
  root.querySelectorAll('media-gallery[data-dazm-initial-slide]').forEach(alignGallery);
}

/**
 * Shows the gallery of the chosen image option value (e.g. the Brown sleeve) without choosing a variant.
 * @param {HTMLElement} picker
 * @param {HTMLInputElement} input
 */
async function showGalleryFor(picker, input) {
  const productUrl = (picker.dataset.productUrl || '').split('?')[0];
  const sectionId = picker.dataset.sectionId;
  if (!productUrl || !sectionId || !input.dataset.optionValueId) return;

  galleryRequests.get(picker)?.abort();
  const controller = new AbortController();
  galleryRequests.set(picker, controller);

  try {
    const response = await fetch(
      productUrl + '?option_values=' + input.dataset.optionValueId + '&section_id=' + encodeURIComponent(sectionId),
      { signal: controller.signal }
    );
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nextGallery = html.querySelector('media-gallery');
    const currentGallery = scopeOf(picker).querySelector('media-gallery');

    // Only swap while the shopper is still choosing (the theme renders the gallery after that).
    if (nextGallery && currentGallery && picker.isConnected && picker.hasAttribute('data-dazm-require-selection')) {
      currentGallery.replaceWith(nextGallery);
    }
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'AbortError')) console.warn('[dazm] gallery update failed', error);
  }
}

/** @param {Event} event */
function onChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

  const picker = input.closest(PICKER_SELECTOR);
  if (!(picker instanceof HTMLElement)) return;

  const missing = missingOptions(picker);

  if (missing.length === 0) {
    // Last option chosen: hand over to the theme with the variant the shopper actually picked.
    galleryRequests.get(picker)?.abort();
    picker.removeAttribute('data-dazm-require-selection');
    const variant = findVariant(picker, chosenOptions(picker));
    if (variant) {
      input.dataset.variantId = String(variant.id);
    } else {
      delete input.dataset.variantId;
    }
    return;
  }

  // Still choosing: keep the theme from picking a variant for the shopper.
  event.stopImmediatePropagation();
  lockBuyButtons(picker);

  if (input.dataset.optionName && input.dataset.optionName === picker.dataset.dazmMediaOption) {
    showGalleryFor(picker, input);
  }
}

/** @param {Event} event */
function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const section = form.closest('.shopify-section');
  if (section && section.querySelector(PICKER_SELECTOR)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}

function init() {
  document.querySelectorAll(PICKER_SELECTOR).forEach((picker) => {
    if (picker instanceof HTMLElement) lockBuyButtons(picker);
  });
  alignGalleriesIn(document);

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) alignGalleriesIn(node);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
}

const flagWindow = /** @type {any} */ (window);
if (!flagWindow.__dazmVariantSelect) {
  flagWindow.__dazmVariantSelect = true;
  document.addEventListener('change', onChange, true);
  document.addEventListener('submit', onSubmit, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}